<template lang="pug">
div
  v-card.round-xl(outlined)
    //- amount details
    div.py-4
      template(v-if="isFetchingInvoice || isFetchingTransactions")
        v-skeleton-loader.px-4(type="list-item-three-line@2")
      template(v-else)
        div.px-3
          //- subtotal
          div.d-flex.align-center.mx-1
            div.text-sm.font-bold.neutral700--text Subtotal
            v-spacer
            div.text-sm.font-bold.neutral700--text {{ invoice.subtotal_amount | displayPrice($currency) }}
          //- adjustment
          div.mt-2.mx-1
            div.d-flex.align-center
              div.text-sm.neutral700--text Adjustment
              v-spacer
              div.text-sm.neutral700--text {{ invoice.invoice_discount || 0 | displayPrice($currency) }}
          //- tax
          div.mt-2.mx-1
            div.d-flex.align-center
              div.text-sm.neutral700--text Tax
              v-spacer
              div.text-sm.neutral700--text {{ invoice.tax_amount || 0 | displayPrice($currency) }}
          //- total
          div.d-flex.align-center.mt-2.mx-1
            div.text-sm.font-bold.neutral700--text Total
            v-spacer
            div.text-sm.font-bold.neutral700--text {{ invoice.total_amount | displayPrice($currency) }}
          v-divider.my-4
          //- amount due
          div.d-flex.align-center.mx-1
            div.text-md.font-semibold.neutral700--text Amount due
            v-spacer
            div.text-md.font-semibold(:class="totalBalance ? 'danger500--text' : 'neutral700--text'") {{ totalBalance | displayPrice($currency) }}
        //- payment list
        v-card.round-xl.mt-4.mx-4(outlined)
          v-card-title.neutral200.pa-2
            div.text-sm.font-medium.neutral700--text Payments
            v-spacer
            y-button.ml-2(v-if="!isCancelled && transactions.length && invoice.total_amount" label="Manage" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" height="24" unset-min-width size="xxs" button-class="px-2" @click="showManagePaymentDialog = true")
            y-button.ml-2(v-if="!isCancelled && invoice.total_amount" label="Add" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" height="24" unset-min-width size="xxs" button-class="px-2" @click="showSelectTransactionTypeDialog = true")
            y-button.ml-2(v-if="isCancelled && canAddRefund" label="Add refund" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" height="24" unset-min-width size="xxs" button-class="px-2" @click="onAddRefund")
          v-divider
          v-card-text.pa-0
            template(v-if="transactions.length")
              template(v-for="(transaction, idx) in transactions")
                v-divider(v-if="idx")
                payment-item(:transaction="transaction" :key="`trx-${idx}`" external-edit :activity-group="type" @edit="onEditTransaction" @updated="onUpdateTransaction" @deleted="onUpdateTransaction")
            template(v-else)
              div.text-xs.neutral500--text.text-center.my-3 No payment record
        //- complete payment later
        template(v-if="!isCancelled")
          div.mt-4.px-4
            template(v-if="totalBalance !== 0")
              y-checkbox.mb-n2(v-model="completePaymentLater")
                template(#label)
                  .text-sm.neutral700--text Complete payment later
            //- create button
            y-button.mt-4(:label="createButtonLabel" size="sm" block :loading="isCreating" :disabled="isCreateDisabled" dark, @click="onClickCreate")
            //- cancel button
            yz-btn.mt-3(v-if="reservationData" :label="cancelButtonLabel" size="lg" secondary, block, @click="showCancelDialog = true")
  .dialogs
    record-payment-dialog(v-if="showRecordPaymentDialog || editingTransaction" :open.sync="showRecordPaymentDialog" :transaction="editingTransaction" :lead="reservationData && reservationData.lead" :mode="recordPaymentMode" :context="type" @updated="onUpdateTransaction" @update:open="onRecordPaymentDialogClose")
    cancel-reservation-preorder-dialog(v-model="showCancelDialog" :type="type" :reservation-data="reservationData" :loading="isCancelling" @confirm="onConfirmCancel" @refund="onProceedWithRefund")
    select-transaction-type-dialog(v-model="showSelectTransactionTypeDialog" :show-refund="hasPayments" @select="onSelectTransactionType")
    manage-payment-dialog(v-if="showManagePaymentDialog" :open.sync="showManagePaymentDialog" :title-prefix="managePaymentTitle" :title-rid="managePaymentRid" external-record-payment :activity-group="type" @updated="onUpdateTransaction" @deleted="onUpdateTransaction" @record-payment="onManageDialogRecordPayment" @edit-payment="onManageDialogEditPayment")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PaymentItem from '@/components/clinic/checkout/PaymentItem.vue'
import RecordPaymentDialog from '@/components/clinic/reservation-preorder/RecordPaymentDialog.vue'
import CancelReservationPreorderDialog from '@/components/clinic/reservation-preorder/CancelReservationPreorderDialog.vue'
import SelectTransactionTypeDialog from '@/components/clinic/reservation-preorder/SelectTransactionTypeDialog.vue'
import ManagePaymentDialog from '@/components/clinic/checkout/ManagePaymentDialog.vue'

export default {
  components: { PaymentItem, RecordPaymentDialog, CancelReservationPreorderDialog, SelectTransactionTypeDialog, ManagePaymentDialog },
  props: {
    type: {
      type: String,
      default: 'reservation'
    },
    reservationData: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create'
    }
  },
  data () {
    return {
      isCreating: false,
      isCancelling: false,
      isFetchingInvoice: false,
      isFetchingTransactions: false,
      showRecordPaymentDialog: false,
      showCancelDialog: false,
      showSelectTransactionTypeDialog: false,
      showManagePaymentDialog: false,
      completePaymentLater: false,
      editingTransaction: null,
      recordPaymentMode: 'payment',
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'reservationPreorder/getLeadPid',
      invoice: 'reservationPreorder/getInvoice',
      transactions: 'reservationPreorder/getTransactions',
    }),
    isCancelled () {
      return this.reservationData?.status === 'cancelled'
    },
    createButtonLabel () {
      return 'Register & add to visit'
    },
    cancelButtonLabel () {
      return this.type === 'reservation' ? 'Cancel reservation' : 'Cancel pre-order'
    },
    cancelEndpoint () {
      const resource = this.type === 'reservation' ? 'reservations' : 'preorders'
      return `/clinics/${this.company.slug}/${resource}/${this.reservationData?.id}/cancel`
    },
    totalPaid () {
      if (!this.invoice.pid) return 0
      if (this.transactions.length === 0) return 0
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount
        return sum
      }, 0)
    },
    totalBalance () {
      if (!this.invoice.pid) return 0
      const totalPayments = this.transactions.reduce((s, t) => t.amount > 0 ? s + t.amount : s, 0)
      const totalRefunds = this.transactions.reduce((s, t) => t.amount < 0 ? s + Math.abs(t.amount) : s, 0)
      if (this.isCancelled) {
        return Math.max(0, totalPayments - totalRefunds)
      }
      const netPaid = totalPayments - totalRefunds
      return Math.max(0, this.invoice.total_amount - netPaid)
    },
    hasInvoiceItems () {
      return (this.invoice.products || []).length > 0
    },
    managePaymentTitle () {
      return this.type === 'reservation' ? 'Reservation' : 'Pre-order'
    },
    managePaymentRid () {
      return this.reservationData?.rid || ''
    },
    hasPayments () {
      return this.transactions.some(t => t.amount > 0)
    },
    canAddRefund () {
      if (!this.invoice.total_amount) return false
      const totalPayments = this.transactions.reduce((sum, t) => t.amount > 0 ? sum + t.amount : sum, 0)
      if (totalPayments <= 0) return false
      const totalRefunds = this.transactions.reduce((sum, t) => t.amount < 0 ? sum + Math.abs(t.amount) : sum, 0)
      return totalRefunds < totalPayments
    },
    isCreateDisabled () {
      if (!this.hasInvoiceItems) return true
      return this.totalBalance !== 0 && !this.completePaymentLater
    },
  },
  mounted () {
    this.fetchTransactions()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchInvoice () {
      if (!this.leadPid) return
      this.isFetchingInvoice = true
      try {
        const resp = await this.$axios.get(`/invoices/${this.leadPid}`)
        this.$store.commit('reservationPreorder/setInvoice', resp.data)
        this.$store.commit('treatment/setInvoice', resp.data)
      } catch {
        // silent
      } finally {
        this.isFetchingInvoice = false
      }
    },
    async fetchTransactions () {
      if (!this.leadPid) return
      this.isFetchingTransactions = true
      try {
        const params = { company: this.company.id, pid: this.leadPid }
        const resp = await this.$axios.get('/transactions', { params })
        this.$store.commit('reservationPreorder/setTransactions', resp.data.results)
        this.$store.commit('treatment/setTransactions', resp.data.results)
      } catch {
        // silent
      } finally {
        this.isFetchingTransactions = false
      }
    },
    onUpdateTransaction () {
      this.fetchInvoice()
      this.fetchTransactions()
    },
    onEditTransaction (transaction) {
      this.editingTransaction = transaction
      this.recordPaymentMode = transaction.amount < 0 ? 'refund' : 'payment'
      this.showRecordPaymentDialog = true
    },
    onRecordPaymentDialogClose (val) {
      if (!val) {
        this.editingTransaction = null
        this.recordPaymentMode = 'payment'
      }
    },
    onManageDialogRecordPayment () {
      this.showSelectTransactionTypeDialog = true
    },
    onManageDialogEditPayment (transaction) {
      this.editingTransaction = transaction
      this.recordPaymentMode = transaction.amount < 0 ? 'refund' : 'payment'
      this.showRecordPaymentDialog = true
    },
    onSelectTransactionType (type) {
      this.recordPaymentMode = type
      this.showRecordPaymentDialog = true
    },
    onAddRefund () {
      this.recordPaymentMode = 'refund'
      this.showRecordPaymentDialog = true
    },
    onClickCreate () {
      this.$emit('completed')
    },
    async onConfirmCancel () {
      this.isCancelling = true
      try {
        await this.$axios.post(this.cancelEndpoint)
        const label = this.type === 'reservation' ? 'Reservation' : 'Pre-order'
        this.showMessage(['success', `${label} cancelled successfully`])
        const tag = this.type === 'reservation' ? 'cancel_reservation' : 'cancel_preorder'
        this.createActivityLog(tag, `${label} #${this.reservationData?.rid} was cancelled`)
        this.showCancelDialog = false
        this.$emit('cancelled')
      } catch (err) {
        const message = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
        this.showMessage(['error', message])
      } finally {
        this.isCancelling = false
      }
    },
    onProceedWithRefund () {
      this.showCancelDialog = false
      this.$emit('proceed-refund')
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: this.type,
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
  },
}
</script>
